import { fg } from '@atlaskit/platform-feature-flags/fg';
import type { MessageActionInvocationStatus } from '@atlassian/conversation-assistant-service-api/types/message-actions';
import { StoreActionLoggableName } from '@atlassian/conversation-assistant-store/controllers/chat/types';

import { createSafeAction, type SafeAction } from './createSafeAction';
import { getAgentMessageById } from './getAgentMessageById';
import type { ChatStoreAction } from './types';

type UpdateActionInvocationStatusArgs = {
	conversationId: string;
	messageId: string;
	invocationId: string;
	status: MessageActionInvocationStatus;
	isReliabilityError?: boolean;
};

export const updateActionInvocationStatus: SafeAction<[UpdateActionInvocationStatusArgs]> =
	createSafeAction(
		StoreActionLoggableName.updateActionInvocationStatus,
		({
			conversationId,
			messageId,
			invocationId,
			status,
			isReliabilityError,
		}: UpdateActionInvocationStatusArgs): ChatStoreAction =>
			async ({ getState }, { getMessages, setMessages }) => {
				const currentConversation = getState().conversations[conversationId];
				if (!currentConversation) {
					return;
				}

				const messages = getMessages(conversationId);
				const agentMessage = getAgentMessageById(messages, messageId);

				if (!agentMessage.result) {
					return;
				}

				const updatedMessages = [...messages];

				// Publish classification with the status so reliability observers see the same outcome.
				updatedMessages[agentMessage.index] = {
					...agentMessage.result,
					...(fg('rovo_update_work_item_reliability_errors_only') &&
						isReliabilityError !== undefined && {
							content: {
								...agentMessage.result.content,
								message: {
									...agentMessage.result.content.message,
									message: {
										...agentMessage.result.content.message.message,
										actions: agentMessage.result.content.message.message.actions?.map((action) =>
											action.invocationId === invocationId
												? { ...action, isReliabilityError }
												: action,
										),
									},
								},
							},
						}),
					invokedActions: {
						...agentMessage.result.invokedActions,
						[invocationId]: status,
					},
				};

				setMessages(conversationId, updatedMessages);
			},
	);
